/**
 * Player resource counters. Talent is a card, not a counter.
 *
 * The money bag is a resource like the crown and the heart — one chip that
 * lands on the ray — and not the numeric balance (GAME-33). The two were one
 * field at the demo, which is what made «сто тысяч» read as a filled ray cell.
 */
export const RESOURCE_KEYS = ['presence', 'support', 'money_bag'] as const;
export type ResourceKey = (typeof RESOURCE_KEYS)[number];

export const RESOURCE_LABELS: Record<ResourceKey, string> = {
  presence: 'Проявленность',
  support: 'Поддержка',
  money_bag: 'Мешочек',
};

export type ResourceState = Record<ResourceKey, number>;

export function emptyResources(): ResourceState {
  return { presence: 0, support: 0, money_bag: 0 };
}

/**
 * Eastern Bazaar money (§40): a numeric balance counted in 100 000 bills.
 *
 * The step is the bill, not the only allowed amount — a Bazaar card may name a
 * multiple («получили доход миллион» is ten bills), so an adjustment is any
 * non-zero multiple of the step, up to a sane ceiling.
 *
 * GAME-33: the balance never touches `resources.money_bag`. Landing on the
 * Bazaar's marked cell grants a bag; the balance moves only when the
 * facilitator awards it for a Bazaar card.
 */
export const MONEY_STEP = 100_000;
export const MONEY_MAX_BILLS_PER_CHANGE = 100;
export const MONEY_LABEL = 'талантов';

/**
 * The sector that trades resources for Talents (GAME-36).
 *
 * Named once, here, rather than compared inline in the service: the exchange
 * is a rule of one sector, and a bare `'bazaar'` scattered through handlers is
 * exactly the hard-coding the board configuration exists to avoid.
 */
export const BAZAAR_SECTOR_ID = 'bazaar';

/** Printed game supports from three to six players; solo is handled by one guest. */
export const MAX_GAME_PARTICIPANTS = 6;

/** Board cell configuration (§32) — data-driven, never `if (cellIndex === n)`. */
export const CELL_TYPES = ['start', 'event', 'talent', 'resource', 'finish'] as const;
export type CellType = (typeof CELL_TYPES)[number];

export type BoardCell = {
  id: string;
  index: number;
  /** Percentage coordinates on the board image, so art can be swapped freely. */
  x: number;
  y: number;
  /**
   * Printed diameter of the cell as a percentage of board width, measured from
   * the print file. The overlay traces the circle instead of covering it.
   */
  diameter?: number;
  sectorId?: string;
  cellType: CellType;
  metadata?: Record<string, unknown>;
};

/**
 * A player's resource lane: one of the six rays running from the ring to the
 * Sun. Six cells, filled from the ring inwards — reaching the Sun is the point
 * of the track, so the last resource lands next to it.
 */
export const LANE_CAPACITY = 6;

/**
 * The chips lying on one player's ray, in the order they were earned (GAME-34).
 *
 * The demo derived this list in the board component by grouping the counters,
 * so a seventh resource re-sorted chips that were already on the board. The ray
 * is authoritative state instead: the first six resources keep their cells and
 * their order, and anything past six only grows the counter.
 *
 * Invariant: `slots.length === min(LANE_CAPACITY, total resources)`.
 */
export type RaySlots = ResourceKey[];

export function creditRaySlot(slots: RaySlots, resource: ResourceKey): RaySlots {
  return slots.length >= LANE_CAPACITY ? slots : [...slots, resource];
}

/**
 * Removes a chip when the facilitator takes a resource back or a gift moves it
 * to another player.
 *
 * The ray only shrinks once the player owns fewer than six resources — until
 * then the six earliest chips still exist and stay where they are. The last
 * chip of the removed type goes first, so correcting a mistake undoes the most
 * recent chip rather than reshuffling the ray's beginning.
 */
export function debitRaySlot(
  slots: RaySlots,
  resource: ResourceKey,
  totalAfterDebit: number,
): RaySlots {
  if (slots.length <= totalAfterDebit) return slots;
  const last = slots.lastIndexOf(resource);
  const index = last === -1 ? slots.length - 1 : last;
  return [...slots.slice(0, index), ...slots.slice(index + 1)];
}

export function totalResources(resources: ResourceState): number {
  return RESOURCE_KEYS.reduce((sum, key) => sum + (resources[key] ?? 0), 0);
}

export function isRayComplete(slots: RaySlots): boolean {
  return slots.length >= LANE_CAPACITY;
}

export type BoardLaneCell = {
  /** Percentage coordinates on the board image, as for a track cell. */
  x: number;
  y: number;
  diameter: number;
};

export type BoardLane = {
  /** Matches `resourceLane` on a participant. */
  index: number;
  /** Ray angle in degrees, kept for tracing back to the print file. */
  angle: number;
  /** Ordered from the ring inwards; the first cell fills first. */
  cells: BoardLaneCell[];
  /**
   * The dot that claims the ray for a player, in their lane colour. It sits in
   * the 6 mm the print leaves between the last lane cell and the ring, which is
   * the only free spot on the ray.
   */
  claim: BoardLaneCell;
};

export type BoardSector = {
  id: string;
  title: string;
  shortTitle: string;
  /** Deck slug whose cards this sector draws from, when it draws cards. */
  deckSlug?: string;
  resource?: ResourceKey;
  taleImage?: string;
  guidance?: string;
  /** §37 — one shared story per sector, revealed once for everybody. */
  sharedStory: boolean;
};

export type BoardConfig = {
  boardImage: string;
  cells: BoardCell[];
  /**
   * Optional: a board seeded before the lanes were read has none, and the
   * overlay simply draws no lanes rather than breaking the session.
   */
  lanes?: BoardLane[];
  sectors: BoardSector[];
  startCellId: string;
  /** §34 — one lap only; kept in config rather than hard-coded. */
  laps: number;
  finishRule: 'reach_finish_cell' | 'complete_lap';
};

/**
 * Where one throw of the dice puts a pawn (GAME-37).
 *
 * The whole finish rule lives here, on purpose. Дарья settled it on the call —
 * «вообще просто вот дело в том, что вот этот сектор Грифон это последний, то
 * есть любое попадание вот в сектор Маяк это всё завершение» — and the exact
 * wording may still move, so the rule has to be one function to change rather
 * than a condition spread across the service and the board component.
 *
 * There is no second lap. A throw that would carry a pawn back into the
 * Lighthouse stops it on the Lighthouse's own start cell and the surplus pips
 * simply burn: «встал здесь, кинул шесть, в маяк ты не перепрыгнешь».
 *
 * The opening move is not a finish: everybody starts standing on that cell, and
 * only a throw that has been all the way round reaches it again.
 */
export type MoveResolution = {
  destination: number;
  finished: boolean;
  /** Pips lost to the Lighthouse. Zero for every ordinary move. */
  burnedPips: number;
};

export function resolveMove(board: BoardConfig, from: number, dice: number): MoveResolution {
  const total = board.cells.length;
  const raw = from + dice;
  const startIndex = Math.max(0, board.cells.findIndex((cell) => cell.id === board.startCellId));
  if (board.finishRule === 'complete_lap' && raw >= total) {
    return { destination: startIndex, finished: true, burnedPips: raw - total };
  }
  return { destination: Math.min(raw, total - 1), finished: false, burnedPips: 0 };
}

/**
 * Where a party is in its opening (GAME-31).
 *
 * `intro` covers everything Дарья described as «погружение»: the Lighthouse
 * story read to the room, then every player taking the Talent they were born
 * with. Only once that is done does the dice appear. Kept as one field so the
 * rule is checked in one place instead of being inferred from «has anybody
 * rolled yet».
 */
export const GAME_PHASES = ['intro', 'playing'] as const;
export type GamePhase = (typeof GAME_PHASES)[number];

/**
 * Why a card ended up in a player's history (GAME-36).
 *
 * The Eastern Bazaar alone hands out a Talent three different ways, and the
 * history has to say which: landing on a «Т» cell, a Bazaar event card that
 * grants one «от вселенной», and trading a resource for one.
 */
export const GAME_CARD_REASONS = [
  'sector_event',
  'talent_cell',
  'start_talent',
  'bazaar_exchange',
  'event_reward',
] as const;
export type GameCardReason = (typeof GAME_CARD_REASONS)[number];

/**
 * How the facilitator closed a sector's event card (GAME-39).
 *
 * `rewarded` is the normal path — Дарья: «я сделала так, чтобы все получали,
 * но для того, чтобы получить, ему нужно какие-то усилия приводить». `skipped`
 * exists because Павел is right that there is a variation: not every card ends
 * in an award, and a session must not stall on one that does not.
 */
export const EVENT_CARD_RESOLUTIONS = ['rewarded', 'skipped'] as const;
export type EventCardResolution = (typeof EVENT_CARD_RESOLUTIONS)[number];

export const GAME_CARD_REASON_LABELS: Record<GameCardReason, string> = {
  sector_event: 'Карта сектора',
  talent_cell: 'Клетка «Т»',
  start_talent: 'Талант от рождения',
  bazaar_exchange: 'Обмен на Базаре',
  event_reward: 'Награда за карту',
};

/** Action log types (§30). */
export const GAME_ACTION_TYPES = [
  'session_started',
  'story_opened',
  'story_closed',
  'start_talent_drawn',
  'dice_rolled',
  'player_moved',
  'card_drawn',
  'card_revealed',
  'resource_changed',
  'resource_transferred',
  'resource_goal_reached',
  'talent_granted',
  'card_resolved',
  'money_changed',
  'participant_removed',
  'session_paused',
  'session_resumed',
  'session_completed',
  /* Демо: игрок закрыл последнюю клетку маршрута. Пишется следом за действием,
     которое её закрыло, и снимается отменой вместе с ним. */
  'player_finished',
] as const;
export type GameActionType = (typeof GAME_ACTION_TYPES)[number];

/**
 * Only these action types can be undone (§30, GAME-40).
 *
 * `card_revealed` is on the list because leaving it off is what broke undo at
 * the demo: turning a card face up wrote an entry nobody could reverse, and
 * since undo only ever looks at the newest entry, the button went dead for the
 * rest of the turn. Turning a card back over is trivially reversible, so it
 * belongs here rather than blocking everything behind it.
 */
export const UNDOABLE_ACTION_TYPES: readonly GameActionType[] = [
  'dice_rolled',
  'player_moved',
  'card_drawn',
  'card_revealed',
  'resource_changed',
  'resource_transferred',
  'talent_granted',
  'card_resolved',
  'money_changed',
];

export function isUndoable(type: GameActionType): boolean {
  return UNDOABLE_ACTION_TYPES.includes(type);
}

/**
 * Entries the server writes *alongside* the action that caused them (GAME-40).
 *
 * «Собрал шесть ресурсов» and «партия завершена» are consequences, not moves:
 * they are written in the same batch as the resource change or the final throw.
 * They have no undo payload of their own, so undo steps over them to reach the
 * real action and marks them undone together with it — otherwise the very
 * action that produced them could never be taken back.
 */
export const MARKER_ACTION_TYPES: readonly GameActionType[] = [
  'resource_goal_reached',
  'session_completed',
  'player_finished',
];

export function isMarkerAction(type: GameActionType): boolean {
  return MARKER_ACTION_TYPES.includes(type);
}

/**
 * Записи, через которые отмена проходит насквозь, ничего с ними не делая.
 *
 * Сказка сектора — не ход и не последствие хода. Ведущий прочитал её комнате
 * вслух; «отменить» это нельзя, и запись в журнале должна остаться такой, как
 * есть. Но и загораживать собой предыдущий ход она не должна.
 *
 * До этого она загораживала. Отмена смотрит только на самую свежую запись, а
 * сказка не отменяемая и не маркер — значит, стоило ведущему прочитать её, и
 * ошибочный бросок, который привёл игрока в этот сектор, откатить было уже
 * нельзя. Ровно та же поломка, что в GAME-40 с `card_revealed`: запись, которую
 * никто не может снять, гасит кнопку до конца хода.
 *
 * Отличие от маркеров именно в этом: маркер снимается вместе с действием,
 * которое его породило, а сказка остаётся — она уже прозвучала.
 */
export const UNDO_TRANSPARENT_ACTION_TYPES: readonly GameActionType[] = [
  'story_opened',
  'story_closed',
];

export function isUndoTransparent(type: GameActionType): boolean {
  return UNDO_TRANSPARENT_ACTION_TYPES.includes(type);
}

export const DICE_MIN = 1;
export const DICE_MAX = 6;

/**
 * Texts the game speaks in, kept as data rather than inside components
 * (GAME-38). Дарья dictated both on the call; they belong to the game, so
 * changing a word must not mean editing JSX.
 */

/**
 * Сказки секторов — дословно, как их передала Дарья.
 *
 * Это отдельный элемент механики, а не подпись к картинке: ведущий читает
 * текст комнате вслух, и от него партия начинается. На показе вместо сказки
 * открывалась сцена сектора, и Дарья сразу это назвала — «правда, это не
 * сказка, да?».
 *
 * Тексты живут здесь, а не в конфигурации доски, по двум причинам. Они
 * принадлежат игре, а не вёрстке: поправить слово не должно означать правку
 * разметки. И доска лежит в базе, засеянной один раз, — новые сказки приехали
 * бы только с пересевом, а пересевать боевую базу нельзя.
 *
 * Перенос строк значимый: сказка читается вслух, и разбивка задаёт дыхание
 * чтения. Поэтому текст хранится как есть и показывается как есть.
 */
/** Перенос строки внутри сказки. Вынесен, чтобы не потеряться при правках файла. */
const NEWLINE = String.fromCharCode(10);

export const SECTOR_TALES: Record<string, string> = {
  lighthouse: [
    'Когда-то, задолго до всех выборов и сомнений,',
    'в момент твоего рождения',
    'в тебе зажглась искра.',
    '',
    'Она не исчезла.',
    'Она — твой внутренний маяк.',
    '',
    'Он светит всегда.',
    'Даже когда ты его не видишь.',
  ].join(NEWLINE),

  dragon: [
    'Ты входишь в густой лес.',
    'Там тихо.',
    'И вдруг появляется он.',
    'Дракон.',
    '',
    'Он не хочет тебя уничтожить.',
    'Он охраняет вход в твою силу.',
    '',
    'Он задаёт один вопрос:',
    '«Ты готов стать больше?»',
  ].join(NEWLINE),

  'castle-key': [
    'Перед тобой старинный замок.',
    'В его залах — маски, которые ты носил.',
    '',
    'Они защищали тебя.',
    'Но теперь закрывают путь к свету.',
    '',
    'Где-то здесь — ключ.',
  ].join(NEWLINE),

  bazaar: [
    'Ты попадаешь на живой, шумный рынок.',
    'Здесь всё в движении.',
    '',
    'Таланты превращаются в ценность.',
    'Идеи — в сделки.',
    'Смелость — в деньги.',
    '',
    'Здесь проверяется:',
    '«Готов ли ты проявляться в реальности?»',
  ].join(NEWLINE),

  'sage-island': [
    'За горизонтом — остров.',
    'Там живёт тот, кто знает ответы.',
    '',
    'Но добраться можно только самому.',
    'Используя всё, что у тебя уже есть.',
  ].join(NEWLINE),

  griffin: [
    'Ты поднимаешься к вершинам.',
    'Там, среди огня и ветра, живёт Грифон — хранитель сокровищ.',
    '',
    'Он не отдаёт золото просто так.',
    'Он ждёт, когда ты признаешь свою силу.',
  ].join(NEWLINE),
};

/** Сказка сектора, если она для него написана. */
export function sectorTale(sectorId: string): string | null {
  return SECTOR_TALES[sectorId] ?? null;
}

/** GAME-34 — shown once, when a player's ray fills its sixth cell. */
export const RESOURCE_GOAL_MESSAGE =
  'Вы собрали все шесть ресурсов и успешно прошли игру';

/**
 * GAME-38 — «Печать Мастера», spoken by a player finishing at the Lighthouse.
 * Quoted verbatim from the end of the printed instruction.
 */
export const MASTER_SEAL_DECLARATION =
  'Я признаю свои таланты, выбираю проявляться, занимаю своё место под солнцем. ' +
  'Я — мастер своих талантов.';

export const MASTER_SEAL_TITLE = 'Печать Мастера';

/*
 * Ведущий за собственным столом.
 *
 * Отдельного одиночного режима у партии нет: ведущий просто занимает игровое
 * место и дальше и ведёт, и ходит. «Сесть за стол» читалось как смена роли —
 * будто управление партией на этом заканчивается, — поэтому кнопка названа
 * действием, а рядом сказано, что роль ведущего остаётся.
 */
export const HOST_SEAT_ACTION_LABEL = 'Участвовать как игрок';

export const HOST_SEAT_ACTION_HINT =
  'Вы останетесь ведущим и будете делать ходы вместе с остальными игроками';

export const HOST_SEAT_ROLE_LABEL = 'Ведущий и игрок';

/** Подпись к счётчику за столом: в списке есть и ведущий, а число — про игроков. */
export const PLAYERS_COUNT_LABEL = 'Игроков';
